import { useEffect, useMemo, useState } from 'react';
import {
  AgentSessionViewer,
  type ExternalSessionData,
} from 'src/components/common/AgentSessionViewer';
import { Card, CardContent } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import {
  CheckIcon,
  CursorClickIcon,
  DesktopIcon,
  PencilSimpleIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import type { OperatorActivity, ToolCallRunResponse } from '@erp/rpc';
import { T } from 'gt-react';
import {
  useSubmitInterviewAnnotation,
  useSubmitInterviewTakeover,
} from 'src/api/rpc/hooks/agent/useOperatorSops';
import {
  buildScreenshotItems,
  buildToolCallMessages,
} from 'src/components/common/agentSessionScreenshots';
import { InterviewAnnotateOverlay } from './InterviewAnnotateOverlay';

interface OperatorLivePaneProps {
  /** Operator activity from the shared stream; null before a session starts. */
  activity: OperatorActivity | null;
  finished: boolean;
  /** True while the run is being started but the Kernel browser hasn't provisioned yet (no
   * live-view URL). Renders a loading placeholder in the live-view slot so the screen opens
   * immediately instead of hanging on the start button. */
  starting?: boolean;
  /** Set on the interview page to enable annotate + takeover on the paused live view. */
  organizationId?: string;
  sessionId?: string | null;
  /** The run's durable per-step trace (screenshots + tool calls + reasoning), fetched from
   * `agentDefinitions.getRunTimeline`. Once the run is `finished` the live browser is gone, so
   * the pane renders THIS instead of a blank iframe — the "view a past run" surface. */
  timelineToolCalls?: ToolCallRunResponse[];
}

// Interview-only interaction modes while the run is paused awaiting input (AI-246):
// - annotate: draw marks over the (inert) live view → sent as guidance.
// - takeover: drive the REAL browser through the interactive live view → gestures captured,
//   then reconciled into durable steps when the human hands control back.
type Mode = 'none' | 'annotate' | 'takeover';

/**
 * Renders the live operator browser + turns for an interview/replay session by
 * feeding `AgentSessionViewer` through its `externalData` seam — the page owns
 * the single shared `useOperatorSessionStream` subscription and passes its
 * `activity` here, so the viewer's own session hooks stay inert (no duplicate
 * stream).
 *
 * On the interview page (org + session supplied), while the run is paused awaiting input, the
 * human can either **Annotate** (draw marks on the inert view) or **Take over** (drive the real
 * browser via the interactive Kernel live view; gestures are captured and reconciled on Done).
 */
export function OperatorLivePane({
  activity,
  finished,
  starting = false,
  organizationId,
  sessionId,
  timelineToolCalls,
}: OperatorLivePaneProps) {
  const [mode, setMode] = useState<Mode>('none');
  const submitAnnotation = useSubmitInterviewAnnotation({
    onSuccess: () => setMode('none'),
  });
  const submitTakeover = useSubmitInterviewTakeover({
    onSuccess: () => setMode('none'),
  });

  const awaiting = activity?.awaitingInput ?? null;
  const canInteract = Boolean(
    awaiting &&
    !finished &&
    organizationId &&
    sessionId &&
    activity?.liveViewUrl,
  );

  // Leave the active mode when the pause clears, so a fresh pause doesn't silently reopen the
  // overlay / leave the browser hot over the next question (syncing to the external stream signal).
  useEffect(() => {
    if (!awaiting) setMode('none');
  }, [awaiting]);

  const externalData = useMemo<ExternalSessionData>(
    () => ({
      status: activity?.status ?? null,
      isComplete: finished,
      liveViewUrl: finished ? null : (activity?.liveViewUrl ?? null),
      // Live: the reasoning/action thread lives in the right-hand chat, so this pane is the live
      // browser only (rendered from `liveViewUrl`). Completed: the live browser is gone, so we
      // render the durable trace — the run's per-step tool calls + their screenshots — as the
      // "view a past interview" surface (nothing existed here before).
      messages: finished ? buildToolCallMessages(timelineToolCalls ?? []) : [],
      screenshotItems: finished
        ? buildScreenshotItems({
            toolCalls: timelineToolCalls ?? [],
            initialScreenshotUrl: null,
            finalLabel: 'Final state',
          })
        : [],
    }),
    [activity?.status, activity?.liveViewUrl, finished, timelineToolCalls],
  );

  // While the Kernel browser is still provisioning (and, for the interview, logging in), there is
  // no live-view URL yet — show a loading placeholder in its slot so the operator screen opens
  // immediately instead of hanging on the start button. Covers both the pre-session start window
  // and any brief gap before the live-view URL arrives on the stream.
  const connecting =
    starting || (activity != null && !activity.liveViewUrl && !finished);

  if (!activity && !connecting) {
    return (
      <Card className="min-h-64">
        <CardContent className="flex h-full flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
          <DesktopIcon className="h-8 w-8" />
          <p className="text-sm">
            <T context="Operator live pane empty state before a session starts">
              Start a session to see live activity.
            </T>
          </p>
        </CardContent>
      </Card>
    );
  }

  if (connecting) {
    return (
      <Card className="min-h-64">
        <CardContent className="flex h-full flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
          <SpinnerIcon className="h-8 w-8 animate-spin" />
          <p className="text-sm">
            <T context="Operator live pane loading state while the browser is provisioning">
              Preparing the browser session…
            </T>
          </p>
        </CardContent>
      </Card>
    );
  }

  const overlay =
    mode === 'annotate' && canInteract ? (
      <InterviewAnnotateOverlay
        pending={submitAnnotation.isPending}
        onCancel={() => setMode('none')}
        onSubmit={({ note, strokes }) => {
          if (!organizationId || !sessionId) return;
          submitAnnotation.mutate({ organizationId, sessionId, note, strokes });
        }}
      />
    ) : undefined;

  return (
    <div className="flex flex-col gap-2">
      {canInteract && mode === 'none' && (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMode('annotate')}
          >
            <PencilSimpleIcon className="mr-1 h-3.5 w-3.5" />
            <T context="Toggle to draw annotations on the paused live view">
              Annotate
            </T>
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMode('takeover')}
          >
            <CursorClickIcon className="mr-1 h-3.5 w-3.5" />
            <T context="Toggle to drive the live browser yourself">Take over</T>
          </Button>
        </div>
      )}
      {mode === 'takeover' && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm">
          <span>
            <T context="Banner shown while the user is driving the live browser">
              You're driving the browser — do the steps, then hand control back.
            </T>
          </span>
          <div className="flex shrink-0 gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setMode('none')}
              disabled={submitTakeover.isPending}
            >
              <T context="Cancel takeover without recording">Cancel</T>
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (!organizationId || !sessionId) return;
                submitTakeover.mutate({ organizationId, sessionId });
              }}
              disabled={submitTakeover.isPending}
            >
              <CheckIcon className="mr-1 h-3.5 w-3.5" />
              <T context="Finish takeover and have the agent record what was done">
                Done — record what I did
              </T>
            </Button>
          </div>
        </div>
      )}
      <AgentSessionViewer
        externalData={externalData}
        liveOverlay={overlay}
        liveInteractive={mode === 'takeover' ? true : undefined}
        // Hide the trajectory while live (the chat pane owns it); show it once finished so the
        // completed pane IS the recorded agent trace (reasoning + actions + screenshots).
        hideMessageThread={!finished}
      />
    </div>
  );
}
